import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { COMMERCE_SOURCES } from '../../services/analyticsService';
import productosService from '../../services/productosService';
import ProductGrid from '../products/ProductGrid';

/**
 * Productos destacados de la portada.
 *
 * Usa la MISMA tarjeta que el catálogo a propósito. Antes esta sección dibujaba
 * su propia tarjeta a mano, y por eso el arreglo que hizo clicable la tarjeta
 * del catálogo (ver el comentario del <Link> en ProductCard.jsx) nunca llegó
 * aquí: en la portada la imagen y el título no llevaban a ningún lado y el
 * único elemento interactivo era el botón de comprar, que saltaba directo al
 * checkout. Se notaba en la analítica — había eventos `buy_now` con origen
 * `home_destacados` pero ni una sola visita a ficha desde la portada.
 */
export default function FeaturedProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await productosService.getProductos({
          limit: 12,
          stock: true,
          order: 'recientes',
          direction: 'desc',
        });
        const list = data.productos || data || [];
        setProducts(list.map(p => productosService.normalizeProduct(p)));
      } catch (err) {
        console.error('Error fetching featured products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <section className="df-section first:pt-[calc(var(--navbar-height)+4rem)] md:first:pt-[calc(var(--navbar-height)+6rem)] bg-df-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-cinzel text-3xl md:text-4xl font-bold text-df-text mb-4">
              Productos <span className="text-df-cyan">Destacados</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="df-card p-4 animate-pulse">
                <div className="bg-df-surface2 h-40 rounded-lg mb-4" />
                <div className="bg-df-surface2 h-4 rounded w-3/4 mb-2" />
                <div className="bg-df-surface2 h-4 rounded w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!products.length) return null;

  return (
    <section id="productos-destacados" className="df-section first:pt-[calc(var(--navbar-height)+4rem)] md:first:pt-[calc(var(--navbar-height)+6rem)] bg-df-surface relative">
      {/* Top glow line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-px bg-gradient-to-r from-transparent via-df-cyan/30 to-transparent" />

      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-12">
          <h2 className="font-cinzel text-3xl md:text-4xl font-bold text-df-text mb-4">
            Productos <span className="text-df-cyan">Destacados</span>
          </h2>
          <p className="text-df-muted max-w-xl mx-auto">
            Los juegos que están marcando tendencia en nuestra comunidad.
          </p>
        </div>

        {/* Misma rejilla que la tienda: si cada página define la suya, las
            tarjetas salen de anchos distintos y con ellas el recuadro de la
            foto, que es cuadrado y sigue al ancho. */}
        <ProductGrid
          products={products.slice(0, 12)}
          source={COMMERCE_SOURCES.HOME_DESTACADOS}
        />

        {/* View all link */}
        <div className="text-center mt-10">
          <Link
            to="/productos"
            className="inline-flex items-center gap-2 px-8 py-3 border border-df-border text-df-text rounded-lg font-medium hover:border-df-cyan hover:text-df-cyan transition-all duration-300"
          >
            Ver todos los productos
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
